import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { setQuoteHeaders } from "../api";
import { toast } from "sonner";
import { effectivePrice, ceil10, calcVolume, uid, DEFAULT_RITIRO_DISCLAIMER, parseGaugePeriods } from "../utils";

const QuoteContext = createContext(null);
export const useQuote = () => useContext(QuoteContext);

const defaultRitiro = (settings) => ({
  enabled: true,
  cap: "",
  citta: "",
  distanzaKm: 0,
  tempoMin: 0,
  isDistanceManual: false,
  isTimeManual: false,
  tariffaOraria: settings?.tariffaOraria ?? 30,
  numPersone: settings?.numPersoneDefault ?? 2,
  imballaggioEnabled: false,
  costoTotaleManual: null,
  isCostoManual: false,
  fachMinutesManual: null,
  isFachManual: false,
  freeEnabled: false,
  freePeriods: [12],
  disclaimer: DEFAULT_RITIRO_DISCLAIMER,
  imballaggio: {
    enabled: false,
    tariffaOraria: settings?.tariffaOraria ?? 25,
    numPersone: settings?.numPersoneDefault ?? 2,
    minutiManuale: null,
    isMinutiManual: false,
    costoManuale: null,
    isCostoManual: false,
    disclaimer: "",
  },
  smontaggio: {
    enabled: false,
    tariffaOraria: settings?.tariffaOraria ?? 25,
    numPersone: settings?.numPersoneDefault ?? 2,
    minutiManuale: null,
    isMinutiManual: false,
    costoManuale: null,
    isCostoManual: false,
    disclaimer: "",
  },
});

const defaultArea = (settings) => ({
  area: 0,
  altezza: 2.2,
  riempimento: settings?.riempimentoDefault ?? 25,
  tariffaM3: settings?.pricePerM3 ?? 72,
  isTariffaOverridden: false,
});

export const QuoteProvider = ({ children }) => {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [catalog, setCatalog] = useState([]);
  const [packingCatalog, setPackingCatalog] = useState([]);
  const [packingEnabled, setPackingEnabled] = useState(false);
  const [packingItems, setPackingItems] = useState([]); // {id,label,cost,client,qty}
  const [settings, setSettings] = useState(null);
  const [discounts, setDiscounts] = useState([]);

  const [mode, setMode] = useState(null);
  const [items, setItems] = useState([]);
  const [ritiro, setRitiro] = useState(defaultRitiro(null));
  const [areaData, setAreaData] = useState(defaultArea(null));
  const [clienteNome, setClienteNome] = useState("");
  const [marketData, setMarketData] = useState([]);
  const [marketDataAt, setMarketDataAt] = useState(null);
  const [areaMarkets, setAreaMarkets] = useState({});
  const [polishedMessage, setPolishedMessage] = useState("");
  const [abitiBox, setAbitiBox] = useState(false);
  const [freeMaterials, setFreeMaterials] = useState(true);
  const [adaptedWhy, setAdaptedWhy] = useState("");
  const [imagePhotos, setImagePhotos] = useState([]);
  const [gaugeToggles, setGaugeToggles] = useState({ transport: true, smontaggio: true, imballaggio: true, packing: true });
  const [inputText, setInputText] = useState("");
  const [analysisImageUrl, setAnalysisImageUrl] = useState("");
  const [chatMessages, setChatMessages] = useState([]); // [{role, content}] per modalità text_simple
  const [chatMessage, setChatMessage] = useState(""); // ultimo messaggio preventivo generato dalla chat

  const [isMonthlyTotalManual, setIsMonthlyTotalManual] = useState(false);
  const [monthlyTotalManual, setMonthlyTotalManual] = useState(0);

  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle|saving|saved|error
  const [savedQuoteNum, setSavedQuoteNum] = useState(null);
  const [quoteGaugePeriods, setQuoteGaugePeriods] = useState(null); // soglie snapshot del preventivo (null = usa impostazioni attuali)
  const [copiedFrom, setCopiedFrom] = useState(null); // numero del preventivo di origine (copia)
  const [copiedFromId, setCopiedFromId] = useState(null);

  const effectiveGaugePeriods = useMemo(
    () => (quoteGaugePeriods && quoteGaugePeriods.length ? quoteGaugePeriods : parseGaugePeriods(settings?.gaugePeriods)),
    [quoteGaugePeriods, settings]
  );

  const [undoStack, setUndoStack] = useState([]);

  // keep the API layer aware of the current stima so AI usage can be linked to it
  useEffect(() => { setQuoteHeaders(currentQuoteId, savedQuoteNum); }, [currentQuoteId, savedQuoteNum]);

  // ---- auth ----
  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    resetQuote();
  };

  useEffect(() => { loadUser(); }, [loadUser]);

  // ---- initial data ----
  const loadCatalog = useCallback(async () => {
    const { data } = await api.get("/catalog");
    setCatalog(data.items || []);
  }, []);
  const loadPackingCatalog = useCallback(async () => {
    const { data } = await api.get("/packing-materials");
    setPackingCatalog(data.items || []);
  }, []);
  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data.settings);
    setRitiro((r) => ({ ...r, tariffaOraria: data.settings.tariffaOraria, numPersone: data.settings.numPersoneDefault }));
    setAreaData((a) => ({ ...a, tariffaM3: a.isTariffaOverridden ? a.tariffaM3 : data.settings.pricePerM3 }));
  }, []);
  const loadDiscounts = useCallback(async () => {
    const { data } = await api.get("/volume-discounts");
    setDiscounts(data.discounts || []);
  }, []);

  useEffect(() => {
    if (user) { loadCatalog(); loadSettings(); loadDiscounts(); loadPackingCatalog(); }
  }, [user, loadCatalog, loadSettings, loadDiscounts, loadPackingCatalog]);

  // ---- computed ----
  const totalVolume = useMemo(
    () => items.reduce((s, it) => s + calcVolume(it.dims.l, it.dims.w, it.dims.h) * it.qty, 0),
    [items]
  );

  const monthlyTotal = useMemo(() => {
    if (isMonthlyTotalManual) return monthlyTotalManual;
    return items.reduce((s, it) => s + effectivePrice(it, discounts) * it.qty, 0);
  }, [items, discounts, isMonthlyTotalManual, monthlyTotalManual]);

  // Volume/canone considerati per il Profit-o-metro (esclude oggetti con countInProfit === false)
  const profitVolume = useMemo(
    () => items.reduce((s, it) => s + (it.countInProfit === false ? 0 : calcVolume(it.dims.l, it.dims.w, it.dims.h) * it.qty), 0),
    [items]
  );
  const profitMonthly = useMemo(() => {
    if (isMonthlyTotalManual) return monthlyTotalManual;
    return items.reduce((s, it) => s + (it.countInProfit === false ? 0 : effectivePrice(it, discounts) * it.qty), 0);
  }, [items, discounts, isMonthlyTotalManual, monthlyTotalManual]);

  // ---- Materiali d'imballaggio (calcolo interno / margine) ----
  const packingCostTotal = useMemo(() => packingItems.reduce((s, i) => s + (Number(i.cost) || 0) * (Number(i.qty) || 0), 0), [packingItems]);
  const packingClientTotal = useMemo(() => packingItems.reduce((s, i) => s + (Number(i.client) || 0) * (Number(i.qty) || 0), 0), [packingItems]);
  const packingMargin = useMemo(() => (packingEnabled ? (packingClientTotal - packingCostTotal) : 0), [packingEnabled, packingClientTotal, packingCostTotal]);

  const addPackingItem = (m) => setPackingItems((list) => {
    const ex = list.find((i) => i.materialId === m.label);
    if (ex) return list.map((i) => (i === ex ? { ...i, qty: i.qty + 1 } : i));
    return [...list, { id: uid(), materialId: m.label, label: m.label, cost: Number(m.cost_eur) || 0, client: Number(m.client_eur) || 0, qty: 1 }];
  });
  const updatePackingItem = (id, patch) => setPackingItems((list) => list.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const removePackingItem = (id) => setPackingItems((list) => list.filter((i) => i.id !== id));
  const setPackingClientTotal = (newTotal) => {
    const cur = packingItems.reduce((s, i) => s + (Number(i.client) || 0) * (Number(i.qty) || 0), 0);
    if (cur <= 0 || !(newTotal >= 0)) return;
    const factor = newTotal / cur;
    setPackingItems((list) => list.map((i) => ({ ...i, client: Math.round((Number(i.client) || 0) * factor * 100) / 100 })));
  };

  const ritiroTotal = useMemo(() => {
    if (!ritiro.enabled) return 0;
    if (ritiro.isCostoManual && ritiro.costoTotaleManual != null) return ritiro.costoTotaleManual;
    const fach = ritiro.isFachManual && ritiro.fachMinutesManual != null
      ? ritiro.fachMinutesManual
      : items.reduce((s, it) => s + (it.fachMinutes || 0) * it.qty, 0);
    const travel = (ritiro.tempoMin || 0) * 2;
    const totalMinutes = travel + fach;
    const totalHours = totalMinutes / 60;
    return totalHours * (ritiro.tariffaOraria || 0) * (ritiro.numPersone || 1);
  }, [ritiro, items]);

  const ritiroBreakdown = useMemo(() => {
    const fach = ritiro.isFachManual && ritiro.fachMinutesManual != null
      ? ritiro.fachMinutesManual
      : items.reduce((s, it) => s + (it.fachMinutes || 0) * it.qty, 0);
    const travel = (ritiro.tempoMin || 0) * 2;
    return { fach, travel, total: travel + fach };
  }, [ritiro, items]);

  const smontaggioMinutes = useMemo(() => {
    const s = ritiro.smontaggio || {};
    if (s.isMinutiManual && s.minutiManuale != null) return s.minutiManuale;
    return items.reduce((sum, it) => sum + (it.includeSmont !== false ? (it.smontMinutes ?? it.imbMinutes ?? 0) * it.qty : 0), 0);
  }, [ritiro.smontaggio, items]);

  const smontaggioTotal = useMemo(() => {
    const s = ritiro.smontaggio || {};
    if (!s.enabled) return 0;
    if (s.isCostoManual && s.costoManuale != null) return s.costoManuale;
    return (smontaggioMinutes / 60) * (s.tariffaOraria || 0) * (s.numPersone || 1);
  }, [ritiro.smontaggio, smontaggioMinutes]);

  const imballaggioMinutes = useMemo(() => {
    const imb = ritiro.imballaggio || {};
    if (imb.isMinutiManual && imb.minutiManuale != null) return imb.minutiManuale;
    return items.reduce((s, it) => s + (it.includeImb !== false ? (it.imbMinutes || 0) * it.qty : 0), 0);
  }, [ritiro.imballaggio, items]);

  const imballaggioTotal = useMemo(() => {
    const imb = ritiro.imballaggio || {};
    if (!imb.enabled) return 0;
    if (imb.isCostoManual && imb.costoManuale != null) return imb.costoManuale;
    return (imballaggioMinutes / 60) * (imb.tariffaOraria || 0) * (imb.numPersone || 1);
  }, [ritiro.imballaggio, imballaggioMinutes]);

  // Auto (non-manual) versions for the profit gauge deltas
  const ritiroAutoTotal = useMemo(() => {
    if (!ritiro.enabled) return 0;
    const fach = ritiro.isFachManual && ritiro.fachMinutesManual != null
      ? ritiro.fachMinutesManual
      : items.reduce((s, it) => s + (it.fachMinutes || 0) * it.qty, 0);
    const totalMinutes = (ritiro.tempoMin || 0) * 2 + fach;
    return (totalMinutes / 60) * (ritiro.tariffaOraria || 0) * (ritiro.numPersone || 1);
  }, [ritiro, items]);

  const smontaggioAutoTotal = useMemo(() => {
    const s = ritiro.smontaggio || {};
    if (!s.enabled) return 0;
    return (smontaggioMinutes / 60) * (s.tariffaOraria || 0) * (s.numPersone || 1);
  }, [ritiro.smontaggio, smontaggioMinutes]);

  const imballaggioAutoTotal = useMemo(() => {
    const imb = ritiro.imballaggio || {};
    if (!imb.enabled) return 0;
    return (imballaggioMinutes / 60) * (imb.tariffaOraria || 0) * (imb.numPersone || 1);
  }, [ritiro.imballaggio, imballaggioMinutes]);

  // ---- undo ----
  const saveUndoState = useCallback((description) => {
    setUndoStack((stack) => {
      const entry = { items: JSON.parse(JSON.stringify(items)), monthlyTotalManual, isMonthlyTotalManual, description };
      const next = [...stack, entry];
      return next.slice(-10);
    });
  }, [items, monthlyTotalManual, isMonthlyTotalManual]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const entry = stack[stack.length - 1];
      setItems(entry.items);
      setMonthlyTotalManual(entry.monthlyTotalManual);
      setIsMonthlyTotalManual(entry.isMonthlyTotalManual);
      return stack.slice(0, -1);
    });
  }, []);

  const canUndo = undoStack.length > 0;
  const undoDescription = canUndo ? undoStack[undoStack.length - 1].description : "";

  // ---- item ops ----
  const makeItemFromCatalog = (c, qty = 1) => ({
    id: uid(),
    tipo: c.label,
    qty,
    dims: { l: c.length_cm, w: c.width_cm, h: c.height_cm },
    pricePerMonth: c.price_eur,
    note: "",
    fachMinutes: c.fach_minutes,
    smontMinutes: c.imb_minutes,
    imbMinutes: c.imb_minutes,
    includeSmont: false,
    includeImb: false,
    isPriceOverridden: false,
    isDimOverridden: false,
    fromCatalog: true,
    countInProfit: c.count_in_profit !== false,
  });

  const addCatalogItem = (c) => {
    saveUndoState(`Aggiunta ${c.label}`);
    setItems((list) => {
      const existing = list.find((i) => i.tipo === c.label && i.fromCatalog && !i.isPriceOverridden);
      if (existing) return list.map((i) => (i.id === existing.id ? { ...i, qty: i.qty + 1 } : i));
      return [...list, makeItemFromCatalog(c)];
    });
  };

  const addItemsBulk = (newItems) => {
    saveUndoState("Aggiunta oggetti");
    setItems((list) => [...list, ...newItems]);
  };

  const addCustomItem = () => {
    saveUndoState("Aggiunta oggetto personalizzato");
    setItems((list) => [...list, {
      id: uid(), tipo: "Nuovo oggetto", qty: 1, dims: { l: 40, w: 40, h: 40 },
      pricePerMonth: 0, note: "", fachMinutes: 0, smontMinutes: 0, imbMinutes: 0,
      includeSmont: false, includeImb: false,
      isPriceOverridden: false, isDimOverridden: true, fromCatalog: false,
    }]);
  };

  // Map extracted entries [{label, qty, catalogMatch}] to QuoteItems.
  // Unknown items (not in catalog) get AI-estimated prices based on the price list.
  const resolveEntries = async (entries) => {
    const findCat = (label) => catalog.find((x) => x.label.toLowerCase() === String(label || "").toLowerCase());
    const hasDims = (d) => d && d.l > 0 && d.w > 0 && d.h > 0;
    // dimension-based lookup: an item matching a catalog entry's L/W/H (order-insensitive)
    // takes that catalog price, e.g. "scatole" 60x40x40 -> catalog "SCATOLA STANDARD".
    const dimsKey = (l, w, h) => [Math.round(l), Math.round(w), Math.round(h)].sort((a, b) => a - b).join("x");
    const catByDims = {};
    catalog.forEach((c) => {
      if (c.length_cm > 0 && c.width_cm > 0 && c.height_cm > 0) {
        const k = dimsKey(c.length_cm, c.width_cm, c.height_cm);
        if (!(k in catByDims)) catByDims[k] = c;
      }
    });
    const findCatByDims = (d) => (hasDims(d) ? catByDims[dimsKey(d.l, d.w, d.h)] : null);

    const built = entries.map((e) => {
      // When the customer states EXACT dimensions, an exact-size catalog match is more
      // reliable than the AI's fuzzy label guess (e.g. a 60x40x40 box is the standard box
      // at €6, not "Scatola grande trasloco" 80x60x50). So dims win over the label match.
      let c = null;
      if (e.dimsFromText && hasDims(e.dims)) c = findCatByDims(e.dims);
      c = c || findCat(e.catalogMatch) || findCat(e.label);
      if (!c && e.dims) c = findCatByDims(e.dims);
      let item;
      if (c) {
        item = makeItemFromCatalog(c, e.qty);
      } else {
        item = {
          id: uid(), tipo: e.label, qty: e.qty, dims: { l: 0, w: 0, h: 0 },
          pricePerMonth: 0, note: "", fachMinutes: 0, smontMinutes: 0, imbMinutes: 0,
          includeSmont: false, includeImb: false,
          isPriceOverridden: false, isDimOverridden: false, fromCatalog: false, _unknown: true,
        };
      }
      // dimensions from the input text take priority
      if (e.dims && (e.dims.l > 0 || e.dims.w > 0 || e.dims.h > 0)) {
        item.dims = { l: e.dims.l || item.dims.l, w: e.dims.w || item.dims.w, h: e.dims.h || item.dims.h };
        item.isDimOverridden = true;
      }
      // dimensions inferred by AI (not stated by the customer, not from catalog) -> flag them
      if (e.dimsEstimated) item.isDimEstimated = true;
      return item;
    });

    // items needing AI: unknown (need price) OR with missing dimensions (not found from catalog/text)
    const needIdx = built.map((_, i) => i).filter((i) => built[i]._unknown || !hasDims(built[i].dims));
    if (needIdx.length) {
      try {
        const payload = needIdx.map((i) => ({ label: built[i].tipo, dims: built[i].dims }));
        const { data } = await api.post("/ai/estimate-prices", { items: payload });
        const byLabel = {};
        (data.estimates || []).forEach((es) => { byLabel[String(es.label).toLowerCase()] = es; });
        needIdx.forEach((i) => {
          const it = built[i];
          const es = byLabel[String(it.tipo).toLowerCase()];
          if (!es) return;
          // did the AI supply a dimension we were missing?
          const filledFromAI =
            (it.dims.l <= 0 && (es.length_cm || 0) > 0) ||
            (it.dims.w <= 0 && (es.width_cm || 0) > 0) ||
            (it.dims.h <= 0 && (es.height_cm || 0) > 0);
          // fill only the missing dimensions, keep those already known
          it.dims = {
            l: it.dims.l > 0 ? it.dims.l : (es.length_cm || 0),
            w: it.dims.w > 0 ? it.dims.w : (es.width_cm || 0),
            h: it.dims.h > 0 ? it.dims.h : (es.height_cm || 0),
          };
          if (it.dims.l > 0 && it.dims.w > 0 && it.dims.h > 0) it.isDimOverridden = true;
          if (filledFromAI) it.isDimEstimated = true;
          if (it._unknown) {
            it.pricePerMonth = es.price_eur;
            it.fachMinutes = es.fach_minutes;
            it.smontMinutes = es.imb_minutes;
            it.imbMinutes = es.imb_minutes;
            it.note = it.note || "Stima AI";
          }
        });
      } catch { /* keep current values */ }
    }
    built.forEach((it) => { delete it._unknown; });

    // Objects with AI-estimated dimensions are priced volumetrically (volume × price/m³
    // from settings), not by the AI's list price. Also a safety net for any item that
    // ends up with full dimensions but no price, so the price is never left at €0.
    const pricePerM3 = settings?.pricePerM3 ?? 72;
    built.forEach((it) => {
      if (it.isPriceOverridden) return;
      const d = it.dims || {};
      if (d.l > 0 && d.w > 0 && d.h > 0 && (it.isDimEstimated || !(it.pricePerMonth > 0))) {
        it.pricePerMonth = ceil10(calcVolume(d.l, d.w, d.h) * pricePerM3);
      }
    });
    return built;
  };

  const replaceItems = (built) => {
    saveUndoState("Rianalisi testo");
    setItems(built);
  };

  const updateItem = (id, patch) => setItems((list) => list.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  // Reassign a quote item to a different catalog article (keeps qty/note/toggles/photo).
  const reassignCatalog = (id, c) => {
    saveUndoState("Cambia articolo");
    setItems((list) => list.map((i) => {
      if (i.id !== id) return i;
      const base = makeItemFromCatalog(c, i.qty);
      return {
        ...base, id: i.id, note: i.note,
        includeSmont: i.includeSmont, includeImb: i.includeImb,
        countInProfit: i.countInProfit, photoId: i.photoId,
      };
    }));
  };
  const setItemQty = (id, qty) => {
    if (qty <= 0) { removeItem(id); return; }
    updateItem(id, { qty });
  };
  const removeItem = (id) => { saveUndoState("Rimozione oggetto"); setItems((list) => list.filter((i) => i.id !== id)); };
  const duplicateItem = (id) => {
    saveUndoState("Duplica oggetto");
    setItems((list) => {
      const it = list.find((i) => i.id === id);
      if (!it) return list;
      return [...list, { ...JSON.parse(JSON.stringify(it)), id: uid() }];
    });
  };

  const setManualTotal = (target) => { saveUndoState("Imposta totale manuale"); setIsMonthlyTotalManual(true); setMonthlyTotalManual(target); };
  const resetManualTotal = () => { saveUndoState("Reset totale"); setIsMonthlyTotalManual(false); };
  const distributeTotal = (target) => {
    saveUndoState("Distribuisci totale");
    const base = items.reduce((s, it) => s + effectivePrice(it, discounts) * it.qty, 0);
    if (base <= 0) return;
    const factor = target / base;
    setItems((list) => list.map((i) => ({ ...i, pricePerMonth: effectivePrice(i, discounts) * factor, isPriceOverridden: true })));
    setIsMonthlyTotalManual(false);
  };

  // ---- area confirm ----
  const confirmArea = () => {
    const volNetto = areaData.area * areaData.altezza * (areaData.riempimento / 100);
    const price = volNetto * areaData.tariffaM3;
    const areaItem = {
      id: uid(),
      tipo: "Deposito per volume",
      qty: 1,
      dims: { l: 0, w: 0, h: 0 },
      pricePerMonth: price,
      note: `${areaData.area} m² × ${areaData.altezza} m × ${areaData.riempimento}%`,
      fachMinutes: 0,
      imbMinutes: 0,
      isPriceOverridden: true,
      isDimOverridden: false,
      fromCatalog: false,
      _volumeM3: volNetto,
    };
    setItems((list) => {
      const others = list.filter((i) => i.tipo !== "Deposito per volume");
      return [areaItem, ...others];
    });
    setMode("area_done");
  };

  // ---- reset / load ----
  const resetQuote = () => {
    setMode(null);
    setItems([]);
    setRitiro(defaultRitiro(settings));
    setAreaData(defaultArea(settings));
    setClienteNome("");
    setMarketData([]);
    setMarketDataAt(null);
    setAreaMarkets({});
    setPolishedMessage("");
    setAbitiBox(false);
    setFreeMaterials(true);
    setAdaptedWhy("");
    setImagePhotos([]);
    setPackingEnabled(false);
    setPackingItems([]);
    setInputText("");
    setAnalysisImageUrl("");
    setChatMessages([]);
    setChatMessage("");
    setIsMonthlyTotalManual(false);
    setMonthlyTotalManual(0);
    setCurrentQuoteId(null);
    setSaveStatus("idle");
    setSavedQuoteNum(null);
    setQuoteGaugePeriods(null);
    setCopiedFrom(null);
    setCopiedFromId(null);
    setUndoStack([]);
  };

  const newQuote = (m) => {
    resetQuote();
    setMode(m);
  };

  const loadSavedQuote = (q) => {
    resetQuote();
    setCurrentQuoteId(q.id);
    setSavedQuoteNum(q.number ?? null);
    setClienteNome(q.cliente_nome || "");
    setMode(q.mode || "objects");
    try { setItems(JSON.parse(q.items_json || "[]")); } catch { setItems([]); }
    try {
      const r = JSON.parse(q.ritiro_json || "{}");
      setRitiro({ ...defaultRitiro(settings), ...r });
    } catch {}
    try {
      const a = JSON.parse(q.area_json || "{}");
      if (a && Object.keys(a).length) setAreaData({ ...defaultArea(settings), ...a });
    } catch {}
    try { setMarketData(JSON.parse(q.market_data_json || "[]")); } catch {}
    setMarketDataAt(q.market_data_at || null);
    try { setAreaMarkets(JSON.parse(q.area_markets_json || "{}")); } catch { setAreaMarkets({}); }
    setPolishedMessage(q.polished_message || "");
    setAbitiBox(!!q.abiti_box);
    setFreeMaterials(q.free_materials === undefined ? true : !!q.free_materials);
    setAdaptedWhy(q.adapted_why || "");
    setPackingEnabled(!!q.packing_enabled);
    try { setPackingItems(JSON.parse(q.packing_items_json || "[]")); } catch { setPackingItems([]); }
    try {
      const ip = JSON.parse(q.image_photos_json || "[]");
      if (Array.isArray(ip) && ip.length) setImagePhotos(ip);
      else {
        const urls = JSON.parse(q.photo_urls_json || "[]");
        setImagePhotos((urls || []).map((u) => ({ id: uid(), url: u })));
      }
    } catch { setImagePhotos([]); }
    setInputText(q.input_text || "");
    setAnalysisImageUrl(q.input_image_url || "");
    try { setChatMessages(JSON.parse(q.chat_messages_json || "[]")); } catch { setChatMessages([]); }
    setChatMessage(q.chat_message || "");
    try {
      const gp = JSON.parse(q.gauge_periods_json || "[]");
      setQuoteGaugePeriods(Array.isArray(gp) && gp.length ? gp : null);
    } catch { setQuoteGaugePeriods(null); }
    setCopiedFrom(q.copied_from ?? null);
    setCopiedFromId(q.copied_from_id ?? null);
  };

  // Duplica un preventivo: nuovo preventivo con gli stessi dati, ma soglie ATTUALI e indicazione di copia
  const duplicateQuote = (q) => {
    loadSavedQuote(q);
    setCurrentQuoteId(null);
    setSavedQuoteNum(null);
    setQuoteGaugePeriods(parseGaugePeriods(settings?.gaugePeriods));
    setCopiedFrom(q.number ?? null);
    setCopiedFromId(q.id ?? null);
    setMode(q.mode || "objects");
    toast.success(q.number ? `Copia del preventivo #${q.number} creata` : "Copia creata");
  };

  // Duplica il preventivo attualmente aperto (mantiene le modifiche correnti), con soglie ATTUALI
  const duplicateCurrent = () => {
    const fromNum = savedQuoteNum;
    const fromId = currentQuoteId;
    setCurrentQuoteId(null);
    setSavedQuoteNum(null);
    setQuoteGaugePeriods(parseGaugePeriods(settings?.gaugePeriods));
    setCopiedFrom(fromNum ?? null);
    setCopiedFromId(fromId ?? null);
    pendingDupSave.current = true; // salva subito la copia (vedi effetto sotto)
  };

  // ---- open shared quote from ?quote=<id> URL ----
  const openedFromUrl = useRef(false);
  useEffect(() => {
    if (!user || !settings || openedFromUrl.current) return;
    const params = new URLSearchParams(window.location.search);
    const qid = params.get("quote");
    if (!qid) return;
    openedFromUrl.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${qid}`);
        if (data && data.quote) {
          loadSavedQuote(data.quote);
          toast.success("Preventivo condiviso aperto");
        }
      } catch {
        toast.error("Preventivo condiviso non trovato o non accessibile");
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("quote");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, settings]);

  // ---- save ----
  const creatingRef = useRef(false);
  const pendingDupSave = useRef(false);
  const buildPayload = useCallback(() => ({
    clienteNome, clienteCap: ritiro.cap, clienteCitta: ritiro.citta, mode,
    items, ritiro, area: areaData,
    totaleMensile: ceil10(monthlyTotal), totaleRitiro: ceil10(ritiroTotal),
    note: "", marketData, marketDataAt, areaMarkets, polishedMessage, abitiBox, freeMaterials, adaptedWhy,
    packingEnabled, packingItems,
    photoUrls: imagePhotos.map((p) => p.url).filter(Boolean),
    imagePhotos: imagePhotos.map(({ id, url }) => ({ id, url })),
    inputText, inputImageUrl: analysisImageUrl,
    chatMessages, chatMessage,
    gaugePeriods: (quoteGaugePeriods && quoteGaugePeriods.length ? quoteGaugePeriods : parseGaugePeriods(settings?.gaugePeriods)),
    copiedFrom, copiedFromId,
  }), [clienteNome, ritiro, mode, items, areaData, monthlyTotal, ritiroTotal, marketData, marketDataAt, areaMarkets, polishedMessage, abitiBox, freeMaterials, adaptedWhy, imagePhotos, inputText, analysisImageUrl, chatMessages, chatMessage, packingEnabled, packingItems, quoteGaugePeriods, settings, copiedFrom, copiedFromId]);

  const persist = useCallback(async (asNew = false) => {
    if (mode === null) return false;
    // prevent concurrent creates (auto-save race) from producing duplicate quotes
    if (!currentQuoteId && !asNew && creatingRef.current) return false;
    setSaveStatus("saving");
    try {
      if (currentQuoteId && !asNew) {
        await api.put(`/quotes/${currentQuoteId}`, buildPayload());
      } else {
        // don't auto-create an empty quote (no items / cliente / testo / immagine)
        const payload = buildPayload();
        const hasContent = (payload.items && payload.items.length) || (payload.clienteNome || "").trim() || (payload.inputText || "").trim() || !!payload.inputImageUrl || (payload.chatMessages && payload.chatMessages.length);
        if (!asNew && !hasContent) { setSaveStatus("idle"); return false; }
        if (!asNew) creatingRef.current = true;
        const { data } = await api.post("/quotes", payload);
        setCurrentQuoteId(data.id);
        setSavedQuoteNum(data.number);
        // congela le soglie del preventivo alla creazione (così future modifiche alle impostazioni non le cambiano)
        setQuoteGaugePeriods(payload.gaugePeriods);
      }
      setSaveStatus("saved");
      qc.invalidateQueries({ queryKey: ["quotes"] });
      return true;
    } catch {
      setSaveStatus("error");
      return false;
    } finally {
      creatingRef.current = false;
    }
  }, [mode, currentQuoteId, buildPayload, qc]);

  const saveNow = () => persist(false);
  const saveAsNew = () => persist(true);

  // salva immediatamente una copia creata con "Duplica con soglie attuali"
  useEffect(() => {
    if (!pendingDupSave.current || currentQuoteId !== null) return;
    pendingDupSave.current = false;
    (async () => {
      const ok = await persist(true);
      if (ok) toast.success(copiedFrom ? `Copia del preventivo #${copiedFrom} creata` : "Copia creata");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedFrom, currentQuoteId]);

  // ---- auto-save (2s debounce) ----
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!user || mode === null) return;
    // don't auto-create an empty quote: only persist when there is real content (or an existing quote)
    const hasContent = items.length > 0 || (clienteNome || "").trim() || (inputText || "").trim() || !!analysisImageUrl || chatMessages.length > 0;
    if (!currentQuoteId && !hasContent) { setSaveStatus("idle"); return; }
    setSaveStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { persist(false); }, 2000);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, ritiro, areaData, mode, clienteNome, marketData, marketDataAt, areaMarkets, polishedMessage, abitiBox, freeMaterials, adaptedWhy, imagePhotos, inputText, analysisImageUrl, chatMessages, chatMessage, packingEnabled, packingItems]);

  const value = {
    user, authLoading, login, logout,
    catalog, settings, setSettings, discounts, loadCatalog, loadSettings, loadDiscounts,
    mode, setMode, items, setItems,
    ritiro, setRitiro, areaData, setAreaData,
    clienteNome, setClienteNome, marketData, setMarketData,
    marketDataAt, setMarketDataAt,
    areaMarkets, setAreaMarket: (key, entry) => setAreaMarkets((m) => ({ ...m, [key]: entry })),
    polishedMessage, setPolishedMessage,
    abitiBox, setAbitiBox,
    freeMaterials, setFreeMaterials,
    adaptedWhy, setAdaptedWhy,
    imagePhotos, setImagePhotos,
    packingCatalog, loadPackingCatalog,
    packingEnabled, setPackingEnabled, packingItems,
    addPackingItem, updatePackingItem, removePackingItem, setPackingClientTotal,
    packingCostTotal, packingClientTotal, packingMargin,
    inputText, setInputText, analysisImageUrl, setAnalysisImageUrl,
    chatMessages, setChatMessages, chatMessage, setChatMessage,
    isMonthlyTotalManual, monthlyTotalManual, setManualTotal, resetManualTotal, distributeTotal,
    monthlyTotal, totalVolume, ritiroTotal, ritiroBreakdown,
    profitMonthly, profitVolume,
    ritiroAutoTotal, smontaggioAutoTotal, imballaggioAutoTotal,
    gaugeToggles, setGaugeToggles,
    imballaggioTotal, imballaggioMinutes,
    smontaggioTotal, smontaggioMinutes,
    currentQuoteId, saveStatus, savedQuoteNum, saveNow, saveAsNew,
    effectiveGaugePeriods, copiedFrom, copiedFromId, duplicateQuote, duplicateCurrent,
    addCatalogItem, addItemsBulk, addCustomItem, updateItem, setItemQty, removeItem, duplicateItem, reassignCatalog,
    confirmArea, resetQuote, newQuote, loadSavedQuote, resolveEntries, replaceItems,
    saveUndoState, undo, canUndo, undoDescription,
    makeItemFromCatalog,
  };

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
};
